import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from 'vue-router'

import {
  isChunkLoadError,
  attemptChunkReload,
  clearedChunkReloadQuery,
} from '@claimshield/shared/router/chunk-reload'

import { useAuthStore } from '@/stores/auth'
import { DRIVER_OIDC } from '@/services/api'
import { DRIVER_PASSKEYS_ENABLED, PICKUP_CAPTURE_ENABLED } from '@/config/features'
import { currentSurface, hostSurface, navigateToSurface } from '@/config/hosts'

const routes: RouteRecordRaw[] = [
  { path: '/', name: 'home', component: () => import('@views/HomeView.vue'), meta: { title: 'Sign in | ClaimShield' } },
  { path: '/dvir/sign-in', name: 'dvir-sign-in', component: () => import('@views/DvirSignInView.vue'), meta: { title: 'Vehicle inspection | ClaimShield' } },
  { path: '/auth/verify/:token', name: 'auth-verify', component: () => import('@views/AuthVerifyView.vue'), meta: { title: 'Signing you in | ClaimShield' } },
  { path: '/dvir', name: 'dvir', component: () => import('@views/DvirView.vue'), meta: { requiresAuth: true, title: 'Vehicle inspection | ClaimShield' } },
  { path: '/my-record', name: 'my-record', component: () => import('@views/MyRecordView.vue'), meta: { requiresAuth: true, title: 'Acknowledgments | ClaimShield' } },
  { path: '/rewards', name: 'rewards', component: () => import('@views/DashboardView.vue'), meta: { requiresAuth: true, requiresRewards: true, title: 'Rewards | ClaimShield' } },
  { path: '/rewards/redeem', name: 'rewards-redeem', component: () => import('@views/RedeemView.vue'), meta: { requiresAuth: true, requiresRewards: true, title: 'Rewards | ClaimShield' } },
  { path: '/rewards/orders', name: 'rewards-orders', component: () => import('@views/OrdersView.vue'), meta: { requiresAuth: true, requiresRewards: true, title: 'Rewards | ClaimShield' } },
  { path: '/dashboard', redirect: '/rewards' },
  { path: '/redeem', redirect: '/rewards/redeem' },
  { path: '/orders', redirect: '/rewards/orders' },
  { path: '/phone-sign-in', name: 'phone-sign-in', component: () => import('@views/PhoneSignInView.vue'), meta: { requiresAuth: true, feature: 'passkeys', title: 'Phone sign-in | ClaimShield' } },
  { path: '/loads', name: 'loads', component: () => import('@views/LoadsView.vue'), meta: { requiresAuth: true, feature: 'pickup', title: 'Your loads | ClaimShield' } },
  { path: '/loads/:id', name: 'load-detail', component: () => import('@views/LoadDetailView.vue'), meta: { requiresAuth: true, feature: 'pickup', title: 'Load details | ClaimShield' } },
  { path: '/mechanic/sign-in', name: 'mechanic-sign-in', component: () => import('@views/mechanic/home/MechanicSignInView.vue'), meta: { door: 'mechanic', title: 'Mechanic sign in | ClaimShield' } },
  { path: '/mechanic', name: 'mechanic', component: () => import('@views/mechanic/MechanicView.vue'), meta: { door: 'mechanic', requiresAuth: true, title: 'Mechanic | ClaimShield' } },
  { path: '/mechanic/vehicles/:id', name: 'mechanic-vehicle', component: () => import('@views/mechanic/VehicleView.vue'), meta: { door: 'mechanic', requiresAuth: true, title: 'Vehicle | ClaimShield' } },
  { path: '/mechanic/vehicles/:id/annual', name: 'mechanic-annual', component: () => import('@views/mechanic/AnnualView.vue'), meta: { door: 'mechanic', requiresAuth: true, title: 'Annual inspection | ClaimShield' } },
  { path: '/mechanic/work-orders/:id', name: 'mechanic-work-order', component: () => import('@views/mechanic/WorkOrderView.vue'), meta: { door: 'mechanic', requiresAuth: true, title: 'Work order | ClaimShield' } },
  { path: '/mechanic/meter', name: 'mechanic-meter', component: () => import('@views/mechanic/MeterView.vue'), meta: { door: 'mechanic', requiresAuth: true, title: 'Meter | ClaimShield' } },
  { path: '/garage/annual/:token', name: 'garage-annual', component: () => import('@views/garage/GarageAnnualView.vue'), meta: { door: 'garage', requiresAuth: false, title: 'Annual inspection | ClaimShield' } },
  { path: '/:pathMatch(.*)*', redirect: { path: '/', query: {} } },
]

export const router = createRouter({ history: createWebHistory(), routes })

router.beforeEach(async (to) => {
  const surface = currentSurface()
  const mechanicHost = surface === 'mechanic'
  const canonicalHost = hostSurface()
  const driverHost = canonicalHost === 'driver'
  const mechanicPath = to.meta.door === 'mechanic' || to.meta.door === 'garage'

  // Cross-surface moves occur only when the user starts a navigation.
  if (driverHost && mechanicPath) {
    navigateToSurface('mechanic', to.path)
    return false
  }
  if (canonicalHost === 'mechanic' && !mechanicPath && to.name !== 'auth-verify' && to.path !== '/') {
    navigateToSurface('driver', to.path)
    return false
  }
  if (mechanicHost && to.path === '/') return { name: 'mechanic' }

  const auth = useAuthStore()
  auth.initialize()
  if (DRIVER_OIDC) await auth.initializeOidc()
  let checkedSession = false
  async function checkSession() {
    if (!checkedSession) {
      await auth.ensureSession()
      checkedSession = true
    }
  }

  if ((to.meta.feature === 'passkeys' && !DRIVER_PASSKEYS_ENABLED) ||
      (to.meta.feature === 'pickup' && !PICKUP_CAPTURE_ENABLED)) return { name: 'home' }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: mechanicPath ? 'mechanic-sign-in' : 'home', query: { redirect: to.fullPath } }
  }

  // Recheck the server launch gate on every rewards entry. A session cached
  // before launch was withdrawn must not admit a fresh navigation.
  if (to.meta.requiresRewards) auth.session = null
  if (auth.isAuthenticated && (to.meta.requiresRewards || mechanicPath || to.name === 'home' || to.name === 'dvir' || to.name === 'my-record' || to.name === 'dvir-sign-in' || to.name === 'mechanic-sign-in')) {
    await checkSession()
  }

  if (to.meta.requiresRewards && !auth.rewardsEnabled) return { name: 'home' }

  if (to.meta.door === 'mechanic' && to.name !== 'mechanic-sign-in' && !auth.mechanicEnabled) {
    if (mechanicHost) {
      navigateToSurface('driver', '/')
      return false
    }
    return { name: 'home' }
  }

  const mechanicOnly = auth.session?.capabilities?.mechanic === true && auth.session.capabilities.driver !== true
  if (mechanicOnly && (to.name === 'dvir' || to.name === 'my-record' || to.name === 'home')) {
    if (driverHost) {
      navigateToSurface('mechanic', '/mechanic')
      return false
    }
    return { name: 'mechanic' }
  }

  if (auth.isAuthenticated && (to.name === 'dvir-sign-in' || to.name === 'mechanic-sign-in')) {
    return { name: mechanicHost ? 'mechanic' : 'home' }
  }

  return true
})

router.onError((error, to) => {
  console.error('[Router] Navigation error:', error)
  if (isChunkLoadError(error)) attemptChunkReload(to.fullPath)
})

router.afterEach((to, _from, failure) => {
  if (failure) return
  document.title = to.name === 'home' && useAuthStore().isAuthenticated
    ? 'Home | ClaimShield'
    : typeof to.meta.title === 'string' ? to.meta.title : 'ClaimShield'
  const cleaned = clearedChunkReloadQuery(to.query)
  if (cleaned) void router.replace({ path: to.path, query: cleaned, hash: to.hash })
})
